import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { RouterModule } from '@angular/router';
import { CategoryViewComponentDef } from '@df/catalog-ui/category/view/category-view.component.def';
import { IListingSortOption } from '@df/catalog-ui/listing/sort/listing-sort';
import { ICategoryDataV3 } from '@df/catalog/category/category';
import { AbModule } from '@df/module-ab/ab.module';
import { AbService } from '@df/module-ab/ab.service';
import { FacetOverridePipe } from '@df/module-facet-url/util/facet-override.pipe';
import { injectTimeoutService } from '@df/ng/timeout.service';
import { AfterContentInitDirectiveService } from '@df/ui/common/after-content-init.directive';
import { DragScrollDirective } from '@df/ui/common/drag-scroll.directive';
import { TranslatePipe } from '@df/ui/translation/translate.pipe';
import { getBlockUrl } from '@icms/core/resource/block/get-block-url';
import cloneDeep from 'lodash-es/cloneDeep';
import { Subscription } from 'rxjs/internal/Subscription';
import { CmsOutletBlockComponent } from '../../../cms/outlet/block/cms-outlet-block.component';
import { StickyFreeComponent } from '../../../common/sticky-free/sticky-free.component';
import { BreadcrumbsComponent } from '../../../ui/common/breadcrumbs.component';
import { ReadMoreComponent } from '../../../ui/display/read-more/read-more.component';
import { ProductListingFiltersComponent } from '../listing/filters/product-lisitng-filters.component';
import { ProductListingComponent } from '../listing/product-listing.component';
import { CategoryDecorator } from './../../model/category/category.decorator';
import { CategoryQuickLinksComponent } from './category-quick-links.component';
import { CategoryHeaderComponent } from './header/category-header.component';
import { NCategoryHeader } from './header/category-header.interface';

@Component({
    selector: 'category-view',
    templateUrl: './category-view.html',
    styleUrl: './category-view.component.scss',
    standalone: true,
    imports: [
        AbModule,
        BreadcrumbsComponent,
        CategoryHeaderComponent,
        CmsOutletBlockComponent,
        CommonModule,
        CategoryQuickLinksComponent,
        DragScrollDirective,
        FacetOverridePipe,
        ProductListingComponent,
        ProductListingFiltersComponent,
        ReadMoreComponent,
        RouterModule,
        StickyFreeComponent,
        TranslatePipe
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class CategoryViewComponent extends CategoryViewComponentDef<CategoryDecorator<ICategoryDataV3>> implements OnInit, OnDestroy {
    isStreamlineTest?: boolean;
    categoryHeaderType: string | undefined;

    private readonly abService = inject(AbService);
    private readonly afterContentInitDirectiveService = inject(AfterContentInitDirectiveService);
    private readonly disposableTimeoutService = injectTimeoutService();

    /**
     * Product listing component
     */
    @ViewChild(ProductListingComponent)
    productListingComponent!: ProductListingComponent;

    protected abServiceSubscription?: Subscription;
    private autoExpandTimeout!: number;

    override async ngOnInit() {
        this.afterContentInitDirectiveService.lock(this);

        const model = await this.app.resources.block?.findOneDirect({
            filter: { url: getBlockUrl(NCategoryHeader.ID, this.model?.categoryHeaderVariant) },
            verbosity: 3
        });

        this.categoryHeaderType = model?.data?.content?.header_type || 'standard';

        super.ngOnInit();

        this.subscriptions.add(this.app.meta.subscribe(() => this.detectChanges()));
    }

    override ngOnDestroy(): void {
        super.ngOnDestroy();
        this.afterContentInitDirectiveService.unlock(this);

        clearTimeout(this.autoExpandTimeout);
        this.abServiceSubscription?.unsubscribe();
    }

    get displayModelViewSwitch(): boolean {
        return !!this.model?.data?.['alt_view_allowed'];
    }

    get links() {
        const exploreLinks = this.model?.data?.explore_links?.sort((a, b) => b.count - a.count);
        const quickLinks = this.model?.data?.quick_links;
        const facetExploreLinks = this.meta?.state?.metaData?.explore_links;
        const facetQuickLinks = this.meta?.state?.metaData?.quick_links;
        const facetLevel = this.meta?.state?.metaData?.level;
        const useQuickLinksOnFacet = this.model?.data?.use_quick_links_on_facets;

        if (!facetLevel && !quickLinks && exploreLinks) {
            return exploreLinks?.slice(0, 13);
        }

        if ((!facetLevel && quickLinks) || (facetLevel && !facetQuickLinks && useQuickLinksOnFacet)) {
            return quickLinks;
        }

        if (facetLevel && facetQuickLinks) {
            return facetQuickLinks;
        }

        if (facetLevel && !facetQuickLinks && !useQuickLinksOnFacet && facetExploreLinks) {
            return facetExploreLinks;
        }
    }

    get hasNoFollow(): boolean | undefined {
        return this.meta.currentRoute?.data?.facet?.robots?.includes('NOINDEX');
    }

    get isPlatformBrowser(): boolean {
        return this.app.isPlatformBrowser;
    }

    protected override onModelChange(): void {
        super.onModelChange();

        this.app.logger.info('AB test for plpGrid');

        this.abServiceSubscription?.unsubscribe();

        this.abServiceSubscription = this.abService.getSubject('plpGrid').subscribe(value => {
            if (this.listing?.size) {
                if (value.variant === 'B') {
                    this.listing.size.setDefaultSize([2, 2, 4, 4]);
                } else {
                    this.listing.size.setDefaultSize(this.moduleConfig.listing.category.defaultSize || [2, 2, 5, 5]);
                }
            }
        });

        this.abServiceSubscription = this.abService.getSubject('plpStreamline').subscribe(value => {
            if (value.variant === 'B') {
                this.isStreamlineTest = true;
            } else {
                this.isStreamlineTest = false;
            }
        });
    }

    protected override getSortOps(): IListingSortOption[] | undefined {
        const copy = cloneDeep(this.getAppConfig().sort) as IListingSortOption[];
        let sort = copy;

        if (this.model?.data?.['use_magento_merchandising']) {
            const recommendedSort = sort?.find(i => i.engine === 'klevu');

            if (recommendedSort) {
                delete recommendedSort.engine;
            }
        } else {
            sort = copy;
        }

        return sort;
    }

    protected override resetListing(): void {
        super.resetListing();
        this.disposableTimeoutService.setTimeout(() => {
            this.afterContentInitDirectiveService.unlock(this);
        }, 5000);
    }
}
